/**
 * Static label tree. Retained as the TYPE ANCHOR for `TpoGenConfig['labels']`;
 * the runtime values come from `fe_config.labels` via `feConfigAdapter`.
 */
export const LABELS = {
    common: {
        breadcrumbRoot: 'Trade Promo Optimization',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        close: 'Close',
        actions: 'Actions',
        noData: 'No Data',
    },
    planIndex: {
        sectionTitle: 'Promotion Planner',
        description: 'Build and manage promotion plans by customer, category, and year',
        breadcrumb: 'Planner',
        createNewPlan: 'Create New Plan',
        myPlan: 'My Plan',
        fields: 'Fields',
        selectFields: 'Select Fields',
        tabs: {
            plans: 'Plans',
            comparisonReports: 'Comparison Reports',
        },
        table: {
            planningGroups: 'Planning Groups',
            createdBy: 'Created By',
            status: 'Status',
            lastUpdated: 'Last Updated',
            dateCreated: 'Date Created',
            planVersion: 'Plan Version',
            plans: 'Plans',
            createNewPlan: 'Create New Plan',
            collapse: 'Collapse',
            expand: 'Expand',
            editPlan: 'Edit Plan',
        },
    },
    planDetail: {
        breadcrumb: 'Planner',
        customer: 'Customer: ',
        category: 'Business Unit: ',
        year: 'Year: ',
        createNewPlan: 'Create New Plan',
        exitWithoutPromoSave: "Kindly save your changes before Continuing. You can't proceed without adding a promo.",
        operationalPlanTooltip: 'Operational Plan',
    },
    editSession: {
        editPlan: 'Edit plan',
        exitEditMode: 'Exit Edit mode',
        sessionTimePrefix: 'Session time: ',
        clickToExtendTooltip: 'Click to extend the session',
        extendTooltip: 'Session ending soon. Click to extend.',
        heldByOtherPrefix: 'Being edited by ',
        footerEditingBy: '{holder} is currently editing',
        viewEventAction: 'View Event',
        viewPromoAction: 'View Promo',
        sessionLostTitle: 'Edit session ended',
        sessionLostBody:
            'Your edit access to this plan has ended and any unsaved changes were not saved. The plan is now read-only.',
        sessionLostOk: 'Continue in view mode',
        editPlanTitle: 'Edit Plan',
        editPlanQuestion: 'Are you sure you want to acquire edit permission for {planName}?',
        takeOverNote: 'You can edit one plan at a time. This will end your edit session on {planName}.',
        extendTitle: 'Extend Session',
        extendQuestion: 'Your edit session for {planName} is ending soon. Do you want to extend it?',
        confirmYes: 'Yes',
    },
    keepFlags: {
        removePrefix: 'Remove ',
        applyQuestion: 'Are you sure you want to apply {action}?',
        removeQuestion: 'Are you sure you want to remove {action}?',
        promoStartedTooltip: 'Keep Dates is not allowed for promotions that have already started.',
        noPromoTooltip: 'Add a promo before using this action.',
        mutuallyExclusiveTooltip: 'Remove the other keep action before applying this one.',
        keepDatesIndicatorTooltip: 'Keep Dates applied',
        keepPromoIndicatorTooltip: 'Keep Event applied',
        confirmYes: 'Yes',
        keepEventTitle: 'Are you sure you want to Keep this Event?',
        keepEventNote: 'Keeping this event will apply the change to all promos associated with this event.',
        keepEventRemoveTitle: 'Are you sure you want to remove Keep Event?',
        keepEventRemoveNote: 'Removing Keep Event will apply the change to all promos associated with this event.',
        keptForEventTooltip: 'The whole event is kept. Remove Keep Event before using this action.',
        keepEventIndicatorTooltip: 'Keep Event applied',
    },
    events: {
        addEvents: 'Add Events',
        addNewEvent: 'Add New Event',
        addEventsFromPlan: 'Add Events from Plan',
        editEvent: 'Edit Event',
        eventDetails: 'Event Details',
        productPortfolio: 'Product Portfolio',
        addEventDetails: 'Add Event Details',
        editEventDetails: 'Edit Event Details',
        addProductDetails: 'Add Product Details',
        editProductDetails: 'Edit Product Details',
        update: 'Update',
        emptyRowActionTooltip: 'Add events to proceed',
    },
    promos: {
        sectionTitle: 'Promotions',
        addPromo: 'Add Promo',
        editPromo: 'Edit Promo',
        addNewPromo: 'Add New Promo',
        savePromo: 'Save Promo',
        updatePromo: 'Update Promo',
        noPromotionsAdded: 'No Promotions Added',
        addPromoDetails: 'Add Promo Details',
        editPromoDetails: 'Edit Promo Details',
        addPromoTab: '+ Add Promo',
        promo: 'Promo',
        refreshUplift: 'Refresh Uplift',
    },
    createPlanModal: {
        titleCreate: 'Create New Plan',
        titleEdit: 'Edit Plan',
        okCreate: 'Create Plan',
        form: {
            planName: 'Plan Name',
            planNamePlaceholder: 'Enter Plan Name',
            planNameRequired: 'Plan Name is required',
            productCategory: 'Product Category',
            productCategoryPlaceholder: 'Select Product Category',
            productCategoryRequired: 'Product Category is required',
            customer: 'Customer',
            customerPlaceholder: 'Select Customer',
            customerRequired: 'Customer is required',
            year: 'Year',
            yearPlaceholder: 'Select Year',
            yearRequired: 'Year is required',
            planVersion: 'Plan Version',
            planVersionPlaceholder: 'Select Plan Version',
            planVersionRequired: 'Plan Version is required',
        },
    },
    comparisonReport: {
        breadcrumb: 'Comparison Report',
        scenarioComparison: 'Scenario Comparison',
        defaultReportName: (planName: string) => `${planName} Comparison`,
        createNewReport: 'Create New Report',
        changeBaseScenario: 'Change Base Scenario',
        dim1Label: 'Customer',
        dim2Label: 'Category',
        changeBaseSubtitle: (dim1: string, dim2: string) =>
            `Select base scenario from the same scope ${dim1} x ${dim2}.`,
        addScenarioTitle: 'Add Scenario',
        addScenarioSubtitle: (max: number) =>
            `Select up to ${max} scenarios to compare against the base scenario from the same Planning Group.`,
        add: 'Add',
        selectKpi: 'Select KPI',
        selectAllKpis: 'Select All',
        deselectAllKpis: 'Deselect All',
        kpiColumnHeader: 'KPI',
        myReport: 'My Report',
        apply: 'Apply',
        selectUpTo5: 'Select upto 5 scenarios',
        addScenarioPlaceholderHeader: 'Add Scenario',
        addScenarioPlaceholderDescription: 'Choose scenarios to compare side by side.',
        baseScenarioPlaceholder: 'Name of Scenario',
        projectName: 'Project Name',
        table: {
            scenarioComparisonLibrary: 'Scenario Comparison Library',
            baseScenario: 'Base Scenario',
            createdBy: 'Created by',
            lastUpdated: 'Last Updated',
            customer: 'Customer',
            category: 'Category',
        },
        actions: {
            duplicate: 'Duplicate',
            deleteReport: 'Delete Report',
        },
        kpis: {
            shipmentBaseUnits: 'Shipment Base Units',
            list: 'List $',
            edlpSpend: 'EDLP Spend $',
            tradeAccruedPercent: 'Trade Accrued %',
            tradeAccrued: 'Trade Accrued $',
            merchSpend: 'Merch Spend $',
            slottingSpend: 'Slotting Spend $',
            totalTradeSpend: 'Total Trade Spend $',
        },
        absToggle: 'Abs',
        percentToggle: '%',
        columns: 'Columns',
        search: 'Search',
        basePrefix: 'Base',
        kpiBreakdownTitle: (kpiName: string) => `${kpiName} - Multiple Scenarios`,
        baseMissingMessage: 'Base scenario was deleted',
        baseMissingDescription:
            'The base scenario for this report no longer exists. Please select a new base scenario to continue.',
        scenariosMissingMessage: (count: number) =>
            `${count} comparison scenario(s) were removed and will show as empty columns.`,
        notifications: {
            saveSuccess: 'Comparison report saved successfully',
            saveError: 'Failed to save comparison report',
            updateSuccess: 'Report updated successfully',
            updateError: 'Failed to update comparison report',
            deleteSuccess: 'Comparison report deleted',
            deleteError: 'Failed to delete comparison report',
            duplicateSuccess: 'Comparison report duplicated',
            duplicateError: 'Failed to duplicate comparison report',
        },
        deleteReportModal: {
            title: (reportName: string) => `Delete "${reportName}"?`,
            unnamedReport: 'this report',
            description: 'This action will permanently delete the report.',
        },
        noFiltersApplied: 'No filters applied. Apply filters to see them here.',
        createReportModal: {
            title: 'Create New Report',
            step1Subtitle: '01: Select scope for your report.',
            step2Subtitle: (dim1: string, dim2: string) => `02: Select Base Scenario from ${dim1} x ${dim2}.`,
            reportName: 'Report Name',
            reportNamePlaceholder: 'Enter Report Name',
            selectPlaceholder: (label: string) => `Select ${label}`,
            requiredMessage: (label: string) => `${label} is required`,
            reportType: 'Report Type',
            reportTypePlaceholder: 'Select Report Type',
            next: 'Next',
            create: 'Create',
            search: 'Search',
            createdByLabel: (createdBy: string, year: string) => `Created by ${createdBy} \u2022 ${year}`,
            planMetadata: (dim1: string, dim2: string, year: string) =>
                [dim1, dim2, year].filter(Boolean).join(' \u2022 '),
            maxLimitReached: 'Maximum limit reached.',
        },
    },
    modals: {
        unsavedPromoChanges: 'Unsaved Promo Changes',
        unsavedEventChanges: 'Unsaved Event Changes',
        unsavedPromoDescription: 'You have unsaved changes to this promo. If you leave now, your changes will be lost.',
        unsavedEventDescription: 'You have unsaved changes to this event. If you leave now, your changes will be lost.',
        continueEditing: 'Continue Editing',
        discardChanges: 'Discard Changes',
        deletePromoSubTitle: 'This will delete the promotion from the event.',
        noPromotionsTitle: 'No Promotions Added',
        deleteEvent: 'Delete Event',
        noPromotionsDescription:
            'No promotions have been created for this event yet. If you proceed, the event will also be permanently deleted.',
        saveAsPlanTitle: 'Save As Plan',
        saveAsPlanQuestion: 'Are you sure you want to save a copy of the Plan {planName}?',
        planNameLabel: 'Plan Name :',
        saveAsOriginalTitle: 'Save as Original',
        saveAsOriginalQuestion: 'Are you sure you want to override the existing plan?',
        saveAsOriginalDescription:
            'Saving this will overwrite the original plan and save the optimized plan as the original plan.',
    },
    optimizePlan: {
        toolbarButton: 'Optimize Plan',
        modalTitle: 'Optimize Plan',
        tabs: {
            scope: 'Scope',
            constraints: 'Constraints',
        },
        runOptimisation: 'Run Optimization',
        scopeSection: 'Scope of Optimization',
        baseInputsSection: 'Base Inputs',
        loadingPlaceholder: 'Loading...',
        constraintsSection: 'Constraints',
        selectLevelPlaceholder: 'Select Level',
        tableError: 'An unexpected error occurred. Please try again later.',
        emptyConstraints: 'Select objective, dimension and start date',
        noConstraintsSelected: 'Add a constraint to get started',
        level: {
            addLevel: 'Add Level',
            allLevelsAdded: 'All levels added',
            removeLevel: 'Remove level',
            removeAttribute: 'Remove attribute',
            loading: 'Loading values...',
            error: 'Could not load values for this level.',
            empty: 'No attributes at this level fall within the selected scope.',
        },
        statusMessages: {
            loadingConstraints: 'Loading constraints...',
            countingPromotions: 'Counting promotions...',
            startingOptimization: 'Starting optimization...',
        },
        selector: {
            buttonLabel: 'Select Constraints',
            searchPlaceholder: 'Search KPI...',
            selectAll: 'Select All',
            unselectAll: 'Deselect All',
            noResults: 'No matching constraints',
            cancel: 'Cancel',
            apply: 'Apply',
        },
        notifications: {
            optimizationFailed: 'Optimization failed. Please try again.',
            infeasibleToast: 'No feasible solution found for this plan.',
            infeasibleTagLabel: 'No solution found',
            infeasibleTooltipFallback: 'No feasible solution found for this plan.',
        },
    },
} as const;

export type Labels = typeof LABELS;
